// React imports
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as XLSX from 'xlsx';

// MUI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import ListSubheader from '@mui/material/ListSubheader';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { grey, green, red, amber } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip, faPlane, faTruck, faTableList, faChevronRight, faChevronDown, faFileExcel } from '@fortawesome/pro-light-svg-icons';

// Local imports
import Header from '../../Header';
import readableId from '../../ReadableId.js';
import currencyToText from '../../CurrencyToText.js';
import { statusToText } from '../../OpportunityRow.utils.js';
import { calcChargeableWeight, getMultiplier } from '../../CalcWeight.js';
import { calcOfferSellAndCost } from '../../utils/opportunityFinancials.js';
import OpportunitiesFilter, { applyFilter } from '../components/OpportunitiesFilter.js';

// readableId() calls .utc(); the plugin is only extended server-side, so the client
// bundle needs it registered here before the quote number can be formatted.
dayjs.extend(utc);

const FILTER_STORAGE_KEY = 'OpportunitiesTable.filter';
// How the table is arranged — what it is grouped by, which statuses are showing, how each is
// ordered — is as much a part of where someone left off as the filter beside it, and it was the
// only half thrown away on a trip to another module. Kept under its own key so a stored filter
// from before this existed still loads.
const VIEW_STORAGE_KEY = 'OpportunitiesTable.view';

function readStoredView() {
    try {
        const stored = sessionStorage.getItem(VIEW_STORAGE_KEY);
        const parsed = stored ? JSON.parse(stored) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
}

const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Excel is handed the number itself rather than the string on screen, so it shows whatever float
// arrives — and a profit whose costs cancel its revenue arrives as 2.27374e-13 rather than 0,
// which reads in the sheet as a real, tiny amount of money. The page never showed that because
// `fraction` rounds it away at two decimals. Round the exported figure to the same place, so the
// sheet says what the page it came from said. Blank stays blank: an amount that does not exist is
// not zero.
function exportAmount(value) {
    if (value === null || value === undefined || typeof value !== 'number' || !isFinite(value)) return '';
    // Rounded through the same formatter the table renders with, rather than toFixed, so the two
    // agree on the half-cent cases they would otherwise round in opposite directions.
    return Number(fraction.format(value).replace(/,/g, ''));
}
const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

const TRANSPORT_ICON = { air: faPlane, land: faTruck, sea: faShip };

// The same win-rate definition the Sales Management dashboard uses, so a hit ratio here
// matches the one shown there rather than being a second, subtly different number.
const SENT_STATUSES = new Set(['sent-to-endcustomer', 'opened-by-customer', 'completed', 'endcustomer-accepted', 'lost', 'endcustomer-rejected']);
const WON_STATUSES = new Set(['completed', 'endcustomer-accepted']);
const LOST_STATUSES = new Set(['lost', 'endcustomer-rejected']);

// The chip palette the rest of the app already uses for these statuses (src/theme.js),
// so a status reads the same colour here as it does on the opportunity list.
const STATUS_CHIP_COLOR = {
    'New': 'chip-request-new',
    'Open': 'chip-request-new',
    'Cost Research': 'chip-submitted',
    'Ready for Sales': 'chip-submitted',
    'Follow-up': 'chip-submitted',
    'Sending...': 'chip-sent-to-endcustomer',
    'Sent to Customer': 'chip-sent-to-endcustomer',
    'Opened by Customer': 'chip-opened-by-customer',
    'Accepted by Customer': 'chip-accepted',
    'Won': 'chip-won',
    'Rejected by Customer': 'chip-lost',
    'Lost': 'chip-lost',
};

// A hit ratio only means something once you say "per what". These are the perspectives
// it can be asked from; picking one regroups the table and recomputes every aggregate.
const PERSPECTIVES = [
    { key: 'none', label: 'No grouping — flat list', group: null, noun: 'groups' },
    { key: 'account', label: 'Customer', group: 'Commercial', noun: 'customers' },
    { key: 'owner', label: 'Salesperson (owner)', group: 'Commercial', noun: 'salespeople' },
    { key: 'team', label: 'Team', group: 'Commercial', noun: 'teams' },
    { key: 'creator', label: 'Creator', group: 'Commercial', noun: 'creators' },
    { key: 'mode', label: 'Mode', group: 'Commercial', noun: 'modes' },
    { key: 'origin', label: 'Origin port', group: 'Route', route: true, noun: 'origins' },
    { key: 'destination', label: 'Destination port', group: 'Route', route: true, noun: 'destinations' },
    { key: 'lane', label: 'Trade lane (port → port)', group: 'Route', route: true, noun: 'lanes' },
    { key: 'origin-country', label: 'Origin country', group: 'Route', route: true, noun: 'countries' },
    { key: 'destination-country', label: 'Destination country', group: 'Route', route: true, noun: 'countries' },
    { key: 'country-lane', label: 'Country lane', group: 'Route', route: true, noun: 'country lanes' },
];

const GROUP_SORTS = [
    { key: 'revenue', label: 'Won revenue' },
    { key: 'profit', label: 'Won profit' },
    { key: 'hitRatio', label: 'Hit ratio' },
    { key: 'sent', label: 'Quotes sent' },
    { key: 'won', label: 'Quotes won' },
    { key: 'label', label: 'Name' },
];

const COLUMNS = [
    { key: 'accountName', label: 'Account' },
    { key: 'mode', label: 'Mode' },
    { key: 'origin', label: 'Origin' },
    { key: 'destination', label: 'Destination' },
    { key: 'creator', label: 'Creator' },
    { key: 'owner', label: 'Owner' },
    { key: 'status', label: 'Status' },
    { key: 'cargo', label: 'Cargo', numeric: true },
    { key: 'revenue', label: 'Revenue', numeric: true },
    { key: 'profit', label: 'Profit', numeric: true },
];

// An opportunity carries its routes at the top level, but the free-text and email
// builders hang them off the offers instead — take both so a row is never blank.
function routesOf(opportunity) {
    return [
        ...(opportunity.routes || []),
        ...((opportunity.offers || []).flatMap(offer => offer.routes || [])),
    ].filter(r => r && (r.source || r.destination));
}

// Port text arrives as "Shanghai - CNSHA"; the table only has room for the name.
function portName(port) {
    return port?.text?.split(' - ')[0] || '';
}

function countryOf(port) {
    const code = port?.countryCode;
    if (!code) return '';
    try {
        return countryNames.of(code.toUpperCase()) || code;
    } catch (e) {
        return code;
    }
}

// Same split the sales-performance page makes: air and land go by their own mode, sea
// separates containerised from loose cargo because the two never compare.
function transportModeLabel(opportunity) {
    const tm = opportunity.transportationMethod;
    if (tm === 'air') return 'Air';
    if (tm === 'land') return 'Land';
    const products = opportunity.products || [];
    const hasLCL = products.some(p => {
        const type = (p.type || '').toLowerCase();
        return type.includes('volume') || type.includes('weight') || type.includes('lcl');
    });
    return hasLCL ? 'LCL' : 'FCL';
}

// Cargo has no single unit across modes: containers count in TEUs, everything else in
// chargeable weight. Return both a number to sort on and the unit it was measured in, so
// a group total only ever adds up figures that mean the same thing.
function calcCargo({ opportunity, organization }) {
    const products = opportunity.products || [];
    if (products.length === 0) return { value: 0, unit: '' };

    if (transportModeLabel(opportunity) === 'FCL') {
        const teu = _.sumBy(products, p => {
            const qty = p.quantity || 1;
            return (p.type || '').toLowerCase().includes('40') ? qty * 2 : qty; // 40' = 2 TEUs
        });
        return { value: teu || 0, unit: 'TEU' };
    }

    // A chargeable weight is an air idea - calcChargeableWeight answers for nothing else and
    // returns null, which is why an LCL or land quote showed an empty Cargo column however
    // much cargo it carried. Sea and land measure by weight or measurement instead, which is
    // what getMultiplier works out, mode by mode.
    const measures = _.compact(products.map(p => {
        if (opportunity.transportationMethod === 'air') {
            const w = calcChargeableWeight({
                product: p,
                transportationMethod: opportunity.transportationMethod,
                allProducts: products,
                round: organization.airRoundChargeableWeight,
            });
            return w ? { val: w.val, unit: w.postfix || '' } : null;
        }
        // getMultiplier reads product.type without checking it is there.
        if (typeof p?.type !== 'string') return null;
        const { multiplier, multiplierText } = getMultiplier({
            product: p,
            transportationMethod: opportunity.transportationMethod,
            allProducts: products,
            round: organization.airRoundChargeableWeight,
        });
        return { val: multiplier || 0, unit: multiplierText || '' };
    })).filter(m => m.val);

    if (measures.length === 0) return { value: 0, unit: '' };
    // Mixed units inside one opportunity would be meaningless to add, so take the
    // dominant one and sum only what shares it.
    const unit = measures[0].unit;
    return { value: _.sumBy(measures.filter(m => m.unit === unit), m => m.val) || 0, unit };
}

function cargoText({ value, unit }) {
    if (!value) return '';
    return `${fraction.format(value)}${unit ? ` ${unit}` : ''}`;
}

// Revenue and cost for a single opportunity. Worked out by the same helper the opportunity's
// own financial summary bar uses, because the two are reporting the same quote's profit and
// this table used to answer differently: it priced at today's exchange rates even when the
// quote carried the rates it was sent at, and counted fields the customer never sees.
function calcRevenueAndCosts({ opportunity, exchangeRates, organization }) {
    const selectedOffer = opportunity.offers?.find(o => opportunity.selected?.includes(o.id)) || opportunity.offers?.[0];
    if (!selectedOffer) return { revenue: 0, costs: 0, gp: 0, hasValue: false };

    const { sell, cost, gp } = calcOfferSellAndCost({ offer: selectedOffer, opportunity, exchangeRates, organization });
    // A quote counts once it has been priced at all, not once it has been priced to a profit.
    // `hasValue` blanks the row and keeps it out of the group totals, and asking for a selling
    // price above zero quietly dropped two kinds of won quote from the money: one priced at zero
    // that still carries supplier costs, and one that prices out negative. The opportunities page
    // counts both, so the same won quotes came to two different totals on two screens. Nothing
    // priced at all is still nothing, and still reads as a dash.
    return { revenue: sell, costs: cost, gp, hasValue: sell !== 0 || cost !== 0 };
}

function OpportunitiesTable() {
    const { org, me, opportunities, accounts, exchangeRates } = useOutletContext();
    const navigate = useNavigate();

    const currency = org.defaults?.currency || 'USD';

    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(50);
    // Restored defensively: a stored grouping that no longer exists, or a status list that came
    // back as something other than a list, falls back to the default rather than rendering an
    // empty table nobody can explain.
    const [ perspective, setPerspective ] = useState(() => {
        const stored = readStoredView().perspective;
        return PERSPECTIVES.some(p => p.key === stored) ? stored : 'none';
    });
    const [ groupSort, setGroupSort ] = useState(() => readStoredView().groupSort || 'revenue');
    const [ expandedGroups, setExpandedGroups ] = useState(() => new Set());
    const [ sort, setSort ] = useState(() => {
        const stored = readStoredView().sort;
        return stored && stored.key ? stored : { key: 'accountName', direction: 'asc' };
    });
    const [ statusFilter, setStatusFilter ] = useState(() => {
        const stored = readStoredView().statusFilter;
        return Array.isArray(stored) ? stored : [];
    });

    // Written from one place rather than threaded through every setter, so a control added later
    // is persisted by being state rather than by remembering to save it.
    useEffect(() => {
        try {
            sessionStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ perspective, groupSort, sort, statusFilter }));
        } catch (e) { /* storage is a convenience, never block the view on it */ }
    }, [perspective, groupSort, sort, statusFilter]);

    const [ filter, setFilter ] = useState((() => {
        const base = { days: 'month', teamMember: me.email, accounts: [], priceQuote: 'all', rateRequest: 'all', taskStatus: 'all', taskType: 'all', tags: [], freetext: '' };
        try {
            const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
            return stored ? JSON.parse(stored) : base;
        } catch (e) {
            return base;
        }
    }));

    function setFilterWithStorage(newFilter) {
        setPage(0);
        setFilter(prev => {
            const resolved = typeof newFilter === 'function' ? newFilter(prev) : newFilter;
            try {
                sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(resolved));
            } catch (e) { /* storage is a convenience, never block the filter on it */ }
            return resolved;
        });
    }

    function onSort(key) {
        setSort(prev => prev.key === key
            ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { key, direction: key === 'accountName' ? 'asc' : 'desc' });
        setPage(0);
    }

    function toggleGroup(key) {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    const liveOpportunities = useMemo(
        () => (opportunities || []).filter(o => o.status !== 'deleted'),
        [opportunities]
    );

    const allRows = useMemo(() => {
        if (!org || !accounts) return [];
        const accountsById = new Map((accounts || []).filter(a => a?.id).map(a => [a.id, a]));
        return applyFilter({ filter, opportunities: liveOpportunities, org, accounts }).map(opportunity => {
            const routes = routesOf(opportunity);
            const firstRoute = routes[0];
            const cargo = calcCargo({ opportunity, organization: org });
            const rc = calcRevenueAndCosts({ opportunity, exchangeRates, organization: org });
            const status = opportunity.status;
            return {
                id: opportunity.id,
                opportunity,
                accountName: accountsById.get(opportunity.account?.id)?.name || '',
                mode: transportModeLabel(opportunity),
                modeIcon: TRANSPORT_ICON[opportunity.transportationMethod] || faShip,
                origin: portName(firstRoute?.source),
                destination: portName(firstRoute?.destination),
                // Every lane the quote covers, not just the first — a single quote can be
                // priced Shanghai, Ningbo and Yantian to Valencia all at once.
                lanes: routes.map(r => ({
                    origin: portName(r.source),
                    destination: portName(r.destination),
                    originCountry: countryOf(r.source),
                    destinationCountry: countryOf(r.destination),
                })),
                extraRoutes: Math.max(routes.length - 1, 0),
                // Emails are too wide for a table this dense — the local part identifies the person.
                creator: (opportunity.createdBy || '').split('@')[0],
                owner: (opportunity.owner || opportunity.createdBy || '').split('@')[0],
                status: statusToText({ opportunity }) || 'New',
                isSent: SENT_STATUSES.has(status),
                isWon: WON_STATUSES.has(status),
                isLost: LOST_STATUSES.has(status),
                cargo: cargo.value,
                cargoUnit: cargo.unit,
                revenue: rc.hasValue ? rc.revenue : null,
                profit: rc.hasValue ? rc.gp : null,
            };
        });
    }, [filter, liveOpportunities, org, accounts, exchangeRates]);

    // Options come from the unfiltered set, so picking one status does not make the others
    // vanish from the menu.
    const statusOptions = useMemo(() => _.sortBy(_.uniq(allRows.map(r => r.status))), [allRows]);

    // Filtering here rather than at each consumer: the counts, the totals, the group
    // aggregates, the pagination and the export all read from rows, and giving each its own
    // copy of the filter is how they drift apart.
    const rows = useMemo(
        () => (statusFilter.length === 0 ? allRows : allRows.filter(r => statusFilter.includes(r.status))),
        [allRows, statusFilter]
    );

    const sortedRows = useMemo(() => {
        const column = COLUMNS.find(c => c.key === sort.key);
        const value = row => {
            const v = row[sort.key];
            if (column?.numeric) return v === null || v === undefined ? -Infinity : v;
            return (v || '').toString().toLowerCase();
        };
        return _.orderBy(rows, [value], [sort.direction]);
    }, [rows, sort]);

    // A quote carries an owner's email; the team lives on the org's user list, stored as
    // "<name>:<id>" the way the users page writes it, so the name is what comes before the colon.
    //
    // Matching on the address alone attributes far less than it should, because a company that
    // changes its email domain keeps the same people: the user list holds the address they have
    // now while years of quotes carry the one they had then. So the name in front of the @ is a
    // fallback — but only where it is unambiguous. If two people on the list share it and sit on
    // different teams, there is no way to tell whose quote is whose, and guessing would put real
    // numbers under the wrong team; those are dropped from the fallback rather than risked.
    const { teamByEmail, teamByLocalPart } = useMemo(() => {
        const byEmail = new Map();
        const byLocal = new Map();
        const ambiguous = new Set();
        for (const user of (org?.users || [])) {
            if (!user?.email) continue;
            const name = ((user.team || '').split(':')[0] || '').trim();
            if (!name) continue;
            const email = user.email.toLowerCase();
            byEmail.set(email, name);
            const local = email.split('@')[0];
            if (byLocal.has(local) && byLocal.get(local) !== name) ambiguous.add(local);
            byLocal.set(local, name);
        }
        for (const local of ambiguous) byLocal.delete(local);
        return { teamByEmail: byEmail, teamByLocalPart: byLocal };
    }, [org]);

    const groups = useMemo(() => {
        if (perspective === 'none') return null;

        // A quote can belong to more than one lane, so route perspectives return a key per
        // lane and the quote is counted under each one it actually covers.
        const keysOf = row => {
            if (perspective === 'account') return [row.accountName || 'No account'];
            if (perspective === 'owner') return [row.owner || 'Unassigned'];
            if (perspective === 'team') {
                // Whoever owns the quote is who it counts for, falling back to its creator the
                // same way the owner column does. Someone with no team of their own still has
                // their quotes counted, under a group that says so.
                const email = (row.opportunity.owner || row.opportunity.createdBy || '').toLowerCase();
                return [teamByEmail.get(email) || teamByLocalPart.get(email.split('@')[0]) || 'No team'];
            }
            if (perspective === 'creator') return [row.creator || 'Unknown'];
            if (perspective === 'mode') return [row.mode];
            const lanes = row.lanes.length > 0 ? row.lanes : [{}];
            const keys = lanes.map(lane => {
                if (perspective === 'origin') return lane.origin || 'Unknown';
                if (perspective === 'destination') return lane.destination || 'Unknown';
                if (perspective === 'lane') return `${lane.origin || 'Unknown'} → ${lane.destination || 'Unknown'}`;
                if (perspective === 'origin-country') return lane.originCountry || 'Unknown';
                if (perspective === 'destination-country') return lane.destinationCountry || 'Unknown';
                return `${lane.originCountry || 'Unknown'} → ${lane.destinationCountry || 'Unknown'}`;
            });
            return _.uniq(keys);
        };

        const byKey = new Map();
        for (const row of sortedRows) {
            for (const key of keysOf(row)) {
                if (!byKey.has(key)) byKey.set(key, []);
                byKey.get(key).push(row);
            }
        }

        const built = [...byKey.entries()].map(([label, groupRows]) => {
            const won = groupRows.filter(r => r.isWon);
            const sent = groupRows.filter(r => r.isSent);
            const lost = groupRows.filter(r => r.isLost);
            // Money is counted on won quotes only — that is the revenue actually earned on
            // this customer/lane, not the value of everything that was ever quoted.
            const revenue = _.sumBy(won, r => r.revenue || 0);
            const profit = _.sumBy(won, r => r.profit || 0);
            const units = _.uniq(won.filter(r => r.cargo).map(r => r.cargoUnit));
            return {
                label,
                rows: groupRows,
                quoted: groupRows.length,
                sent: sent.length,
                won: won.length,
                lost: lost.length,
                hitRatio: sent.length > 0 ? Math.round((won.length / sent.length) * 100) : null,
                revenue,
                profit,
                margin: revenue > 0 ? (profit / revenue) * 100 : null,
                // Adding TEUs to kilos would be nonsense — only total a uniform group.
                cargo: units.length === 1 ? { value: _.sumBy(won, r => r.cargo || 0), unit: units[0] } : null,
            };
        });

        if (groupSort === 'label') return _.orderBy(built, [g => g.label.toLowerCase()], ['asc']);
        return _.orderBy(built, [g => g[groupSort] === null ? -Infinity : g[groupSort]], ['desc']);
    }, [sortedRows, perspective, groupSort, teamByEmail, teamByLocalPart]);

    const pageRows = useMemo(
        () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedRows, page, rowsPerPage]
    );

    // Export what is on screen: the filtered rows in their current order, plus the
    // per-group aggregates when a perspective is active, so the numbers in the sheet are
    // the ones the page was showing rather than a second calculation.
    function onExport() {
        const perspectiveLabel = PERSPECTIVES.find(p => p.key === perspective)?.label || 'Group';
        const detailRows = groups === null
            ? sortedRows.map(row => ({ row }))
            : groups.flatMap(group => group.rows.map(row => ({ row, group: group.label })));

        const detail = detailRows.map(({ row, group }) => ({
            ...(groups === null ? {} : { [perspectiveLabel]: group }),
            Quote: readableId(row.opportunity),
            Account: row.accountName,
            Mode: row.mode,
            Origin: row.origin,
            Destination: row.destination,
            // Excel has room for the full address even though the table truncates it.
            Creator: row.opportunity.createdBy || '',
            Owner: row.opportunity.owner || row.opportunity.createdBy || '',
            Status: row.status,
            Cargo: row.cargo || '',
            'Cargo unit': row.cargoUnit || '',
            [`Revenue (${currency})`]: exportAmount(row.revenue),
            [`Profit (${currency})`]: exportAmount(row.profit),
            Created: row.opportunity.created ? dayjs(row.opportunity.created).format('YYYY-MM-DD') : '',
        }));

        const workbook = XLSX.utils.book_new();
        if (groups !== null) {
            const summary = groups.map(group => ({
                [perspectiveLabel]: group.label,
                Quotes: group.quoted,
                Sent: group.sent,
                Won: group.won,
                Lost: group.lost,
                'Hit ratio %': group.hitRatio === null ? '' : group.hitRatio,
                'Won cargo': group.cargo ? exportAmount(group.cargo.value) : '',
                'Cargo unit': group.cargo ? group.cargo.unit : '',
                [`Won revenue (${currency})`]: exportAmount(group.revenue),
                [`Won profit (${currency})`]: exportAmount(group.profit),
                'Margin %': group.margin === null ? '' : Number(group.margin.toFixed(2)),
            }));
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summary), 'Summary');
        }
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(detail), 'Opportunities');
        XLSX.writeFile(workbook, `tradelines-${org.alias || 'export'}-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    }

    if (!opportunities || !accounts) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '64px' }}>
                <Typography variant='caption' color='text.secondary'>Loading...</Typography>
            </Box>
        );
    }

    const wonRows = rows.filter(r => r.isWon);
    const sentRows = rows.filter(r => r.isSent);
    const totals = {
        revenue: _.sumBy(wonRows, r => r.revenue || 0),
        profit: _.sumBy(wonRows, r => r.profit || 0),
        hitRatio: sentRows.length > 0 ? Math.round((wonRows.length / sentRows.length) * 100) : null,
    };
    const isRoutePerspective = PERSPECTIVES.find(p => p.key === perspective)?.route;
    const multiLaneCount = isRoutePerspective ? rows.filter(r => r.lanes.length > 1).length : 0;

    function hitRatioColor(value) {
        if (value === null) return grey[600];
        return value >= 50 ? green[700] : value >= 25 ? amber[800] : red[500];
    }

    function renderRow(row, groupLabel) {
        return (
            <TableRow
                key={groupLabel ? `${groupLabel}-${row.id}` : row.id}
                hover
                sx={{ cursor: 'pointer', '& td': { fontSize: '12px' } }}
                onClick={() => navigate(`../opportunities/${row.opportunity.id}`)}
            >
                <TableCell sx={{ fontWeight: 600 }}>{row.accountName}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <FontAwesomeIcon icon={row.modeIcon} style={{ marginRight: '6px', color: grey[600] }} />
                    {row.mode}
                </TableCell>
                <TableCell>{row.origin}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.destination}
                    {row.extraRoutes > 0 && (
                        <Tooltip title={row.lanes.map(l => `${l.origin || '?'} → ${l.destination || '?'}`).join(', ')}>
                            <Typography component='span' variant='caption' sx={{ color: grey[600], marginLeft: '6px' }}>
                                +{row.extraRoutes}
                            </Typography>
                        </Tooltip>
                    )}
                </TableCell>
                <TableCell>{row.creator}</TableCell>
                <TableCell>{row.owner}</TableCell>
                <TableCell>
                    <Chip
                        size='small'
                        label={row.status}
                        color={STATUS_CHIP_COLOR[row.status] || 'chip-request-new'}
                        sx={{ fontSize: '10px', height: '20px' }}
                    />
                </TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{cargoText({ value: row.cargo, unit: row.cargoUnit }) || '—'}</TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{row.revenue === null ? '—' : currencyToText(currency, true, fraction.format(row.revenue))}</TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: row.profit === null ? 'inherit' : row.profit >= 0 ? green[700] : red[500] }}>
                    {row.profit === null ? '—' : currencyToText(currency, true, fraction.format(row.profit))}
                </TableCell>
            </TableRow>
        );
    }

    return (
        <Box sx={{ paddingBottom: '36px' }}>
            <Header
                icon={faTableList}
                title='Tradelines'
                buttons={[{
                    title: 'Export to Excel',
                    icon: <FontAwesomeIcon icon={faFileExcel} />,
                    action: onExport,
                    disabled: rows.length === 0,
                }]}
            />
            <Box sx={{ padding: '12px', paddingTop: '0px' }}>
                <OpportunitiesFilter
                    filter={filter}
                    setFilter={setFilterWithStorage}
                    me={me}
                    org={org}
                    opportunities={liveOpportunities}
                    accounts={accounts}
                    groupedDays={true}
                    hideTaskStatus={true}
                />
            </Box>
            <Paper sx={{ margin: '0px 12px', border: `1px solid ${grey[300]}` }} elevation={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', padding: '12px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Typography variant='caption' sx={{ fontWeight: 500 }}>
                        <b>{rows.length}</b> {rows.length === 1 ? 'opportunity' : 'opportunities'}
                    </Typography>
                    <Tooltip title='Won quotes out of all sent quotes — pending quotes stay in the denominator'>
                        <Typography variant='caption' sx={{ color: hitRatioColor(totals.hitRatio), fontWeight: 600 }}>
                            Hit ratio {totals.hitRatio === null ? '—' : `${totals.hitRatio}%`}
                        </Typography>
                    </Tooltip>
                    <Typography variant='caption' sx={{ color: grey[700] }}>
                        Won revenue <b>{currencyToText(currency, true, fraction.format(totals.revenue))}</b>
                    </Typography>
                    <Typography variant='caption' sx={{ color: totals.profit >= 0 ? green[700] : red[500] }}>
                        Won profit <b>{currencyToText(currency, true, fraction.format(totals.profit))}</b>
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant='caption' sx={{ color: grey[700] }}>Status</Typography>
                    <Select
                        size='small'
                        multiple
                        displayEmpty
                        value={statusFilter}
                        onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }}
                        renderValue={selected => selected.length === 0
                            ? 'All statuses'
                            : selected.length === 1 ? selected[0] : `${selected.length} selected`}
                        sx={{ fontSize: '13px', minWidth: '150px' }}
                    >
                        {statusOptions.map(option => (
                            <MenuItem key={option} value={option} sx={{ fontSize: '13px' }}>
                                <Checkbox size='small' checked={statusFilter.includes(option)} />
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography variant='caption' sx={{ color: grey[700] }}>Hit ratio per</Typography>
                    <Select
                        size='small'
                        value={perspective}
                        onChange={(event) => { setPerspective(event.target.value); setExpandedGroups(new Set()); setPage(0); }}
                        sx={{ fontSize: '13px', minWidth: '210px' }}
                    >
                        {PERSPECTIVES.flatMap((option, index) => {
                            const startsGroup = option.group && option.group !== PERSPECTIVES[index - 1]?.group;
                            return _.compact([
                                startsGroup && <ListSubheader key={`${option.group}-header`} sx={{ fontSize: '11px', lineHeight: '28px' }}>{option.group}</ListSubheader>,
                                <MenuItem key={option.key} value={option.key} sx={{ fontSize: '13px' }}>{option.label}</MenuItem>,
                            ]);
                        })}
                    </Select>
                    {perspective !== 'none' && (
                        <>
                            <Typography variant='caption' sx={{ color: grey[700] }}>
                                Sort {PERSPECTIVES.find(p => p.key === perspective)?.noun || 'groups'} by
                            </Typography>
                            <Select
                                size='small'
                                value={groupSort}
                                onChange={(event) => setGroupSort(event.target.value)}
                                sx={{ fontSize: '13px', minWidth: '140px' }}
                            >
                                {GROUP_SORTS.map(option => (
                                    <MenuItem key={option.key} value={option.key} sx={{ fontSize: '13px' }}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </>
                    )}
                </Box>
                {perspective !== 'none' && (
                    <Box sx={{ padding: '6px 12px', background: grey[50], borderBottom: `1px solid ${grey[200]}` }}>
                        <Typography variant='caption' sx={{ color: grey[700] }}>
                            Group totals count won quotes only; hit ratio is won ÷ sent.
                            {multiLaneCount > 0 && ` ${multiLaneCount} ${multiLaneCount === 1 ? 'quote covers' : 'quotes cover'} more than one lane and ${multiLaneCount === 1 ? 'is' : 'are'} counted under each.`}
                        </Typography>
                    </Box>
                )}
                <TableContainer>
                    <Table size='small' stickyHeader>
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map(column => (
                                    <TableCell
                                        key={column.key}
                                        align={column.numeric ? 'right' : 'left'}
                                        sortDirection={sort.key === column.key ? sort.direction : false}
                                        sx={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}
                                    >
                                        <TableSortLabel
                                            active={sort.key === column.key}
                                            direction={sort.key === column.key ? sort.direction : 'asc'}
                                            onClick={() => onSort(column.key)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups === null && pageRows.map(row => renderRow(row))}

                            {groups !== null && groups.map(group => {
                                const expanded = expandedGroups.has(group.label);
                                return [
                                    <TableRow
                                        key={`group-${group.label}`}
                                        hover
                                        sx={{ cursor: 'pointer', background: grey[100], '& td': { fontSize: '12px', fontWeight: 700 } }}
                                        onClick={() => toggleGroup(group.label)}
                                    >
                                        <TableCell colSpan={7}>
                                            <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} style={{ marginRight: '8px', color: grey[600] }} />
                                            {group.label}
                                            <Typography component='span' variant='caption' sx={{ color: grey[600], marginLeft: '10px', fontWeight: 500 }}>
                                                {group.sent} sent · {group.won} won · {group.lost} lost
                                            </Typography>
                                            <Typography component='span' variant='caption' sx={{ marginLeft: '10px', fontWeight: 700, color: hitRatioColor(group.hitRatio) }}>
                                                {group.hitRatio === null ? '—' : `${group.hitRatio}% hit`}
                                            </Typography>
                                            {group.margin !== null && (
                                                <Typography component='span' variant='caption' sx={{ color: grey[600], marginLeft: '10px', fontWeight: 500 }}>
                                                    {fraction.format(group.margin)}% margin
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{group.cargo ? cargoText(group.cargo) : '—'}</TableCell>
                                        <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{currencyToText(currency, true, fraction.format(group.revenue))}</TableCell>
                                        <TableCell align='right' sx={{ whiteSpace: 'nowrap', color: group.profit >= 0 ? green[700] : red[500] }}>
                                            {currencyToText(currency, true, fraction.format(group.profit))}
                                        </TableCell>
                                    </TableRow>,
                                    ...(expanded ? group.rows.map(row => renderRow(row, group.label)) : []),
                                ];
                            })}

                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={COLUMNS.length} sx={{ textAlign: 'center', padding: '24px' }}>
                                        <Typography variant='caption' color='text.secondary'>
                                            No opportunities match this filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {perspective === 'none' && (
                    <TablePagination
                        component='div'
                        count={sortedRows.length}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[25, 50, 100, 250]}
                        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
                    />
                )}
            </Paper>
        </Box>
    );
}

export default OpportunitiesTable;
export { calcRevenueAndCosts };
